import {Directive, ElementRef, Input} from '@angular/core';
import {BehaviorSubject, fromEvent, merge, NEVER, Observable} from 'rxjs';
import {map, share, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {WindowRefService} from '../../services/window-ref.service';
import {destroy$} from '../destroyable';
import {ResizeObserverService} from '../resize-observer';

interface ElementRulerOptions {
    updateOn?: Observable<any>;
    updateOnWindowResize?: boolean;
}

type Point = [number, number];

interface Dimensions {
    pos: Point;
    size: Point;
}

@Directive({
    selector: '[elementRuler]',
    exportAs: 'elementRuler',
    standalone: false
})
export class ElementRulerDirective {

    public readonly destroy$ = destroy$();

    // String type allows to omit value in template: <div elementRuler>
    @Input('elementRuler')
    public set options(value: ElementRulerOptions | Observable<any> | string) {
        let updateOn$: Observable<any>;

        if (value) {
            if ((value as Observable<any>).subscribe) {
                updateOn$ = value as Observable<any>;
            } else {
                updateOn$ = (value as ElementRulerOptions).updateOn;

                if ((value as ElementRulerOptions).updateOnWindowResize) {
                    updateOn$ = updateOn$
                        ? merge(updateOn$, this.getWindowResizingStream())
                        : this.getWindowResizingStream();
                }

                updateOn$ = updateOn$ || NEVER;
            }
        } else {
            updateOn$ = this.getWindowResizingStream();
        }

        this.updateOn$.next(updateOn$);
    }

    public size$: Observable<Point>;
    public position$: Observable<Point>;
    public dimensions$: Observable<Dimensions>;

    private updateOn$: BehaviorSubject<Observable<any>> = new BehaviorSubject<Observable<any>>(
        NEVER
    );

    private element: Element = this.elementRef.nativeElement;

    private cachedSize: Point;

    constructor(
        public elementRef: ElementRef,
        private windowRefService: WindowRefService,
        private resizeObserverService: ResizeObserverService
    ) {
        const commonOutput$: Observable<ResizeObserverEntry | undefined> = merge(
            this.resizeObserverService.observe(this.element),
            this.updateOn$.pipe(
                switchMap(updateOn$ => updateOn$),
                map((): undefined => undefined)
            )
        ).pipe(
            shareReplay({bufferSize: 1, refCount: true}),
            takeUntil(this.destroy$)
        );

        this.size$ = commonOutput$.pipe(
            map(entry => this.getSize(entry)),
            share()
        );
        this.position$ = commonOutput$.pipe(
            map(() => this.getPosition()),
            share()
        );
        this.dimensions$ = commonOutput$.pipe(
            map(() => this.getDimensions()),
            share()
        );
    }

    public getSize(entry?: ResizeObserverEntry): Point {
        const size: Point = (entry)
            ? [entry.contentRect.width, entry.contentRect.height]
            : this.cachedSize || [this.element.clientWidth, this.element.clientHeight];

        this.cachedSize = size;

        return size;
    }

    public getPosition(): Point {
        const rect: ClientRect = this.elementRef.nativeElement.getBoundingClientRect();

        return [rect.left, rect.top];
    }

    public getDimensions(): Dimensions {
        const rect: ClientRect = this.elementRef.nativeElement.getBoundingClientRect();

        return {
            pos: [rect.left, rect.top],
            size: [rect.width, rect.height]
        };
    }

    private getWindowResizingStream(): Observable<any> {
        return fromEvent(this.windowRefService.nativeWindow, 'resize');
    }

}
